import { Skeleton } from '@/components/ui/skeleton';

export interface Column<T> {
  header: string;
  cell: (row: T) => React.ReactNode;
  className?: string;
}

/**
 * Minimal shared table for the user-detail domain tabs — the project has no
 * DataTable primitive, and every domain here renders the same shape (a small
 * scoped list with loading / empty states).
 */
function DataTable<T>({
  columns,
  rows,
  keyFn,
  loading,
  emptyLabel = 'Nothing to show.',
  onRowClick,
}: {
  columns: Column<T>[];
  rows: T[];
  keyFn: (row: T) => string | number;
  loading?: boolean;
  emptyLabel?: string;
  onRowClick?: (row: T) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map(col => (
              <th
                key={col.header}
                className={`px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 ${col.className ?? ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <tr key={i}>
                {columns.map(col => (
                  <td key={col.header} className="px-4 py-3">
                    <Skeleton className="h-4 w-20" />
                  </td>
                ))}
              </tr>
            ))}

          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-400">
                {emptyLabel}
              </td>
            </tr>
          )}

          {!loading &&
            rows.map(row => (
              <tr
                key={keyFn(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : undefined}
              >
                {columns.map(col => (
                  <td key={col.header} className={`px-4 py-3 text-gray-900 ${col.className ?? ''}`}>
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
